import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { IPointOfSaleItem, IReservation, IRoom } from '../../../interfaces/IPartnerPms';
import { useCallback, useEffect, useMemo } from 'react';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import { ActionButton } from '../../Button';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import FormHelper from '../../../helpers/FormHelper';
import PartnerPmsService from '../../../services/PartnerPmsService';
import Empty from '../../Empty';
import { Add, Remove, SearchOff, Search as SearchOn } from '@mui/icons-material';
import Badge from '../../Badge';
import useDialog from '../../../hooks/useDialog';
import ViewShoppingCartDialog from './ViewShoppingCartDialog';
import useSearch from '../../../hooks/useSearch';
import Search from '../../Search';
import StringHelper from '../../../helpers/StringHelper';
import { useAuthContext } from '../../../contexts/AuthContext';
import useState from '../../../hooks/useState';
import PointOfSaleItemsList from '../../ItemsList/PointOfSaleItemsList';



interface IRoomServiceItemsListDialogProps extends IDefaultDialogProps {
    room: IRoom;
    reservation: IReservation;
}

interface IStateValues {
    items: IPointOfSaleItem[];
    shoppingCart: Record<string, IPointOfSaleItem>;
}


const RoomServiceItemsListDialog: React.FC<IRoomServiceItemsListDialogProps> = ({
    open,
    handleClose,
    room,
    reservation
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        items: [],
        shoppingCart: {}
    });

    const search = useSearch();
    const viewShoppingCartDialog = useDialog();


    useEffect(() => {
        state.update('shoppingCart', {});
        search.reset();
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'pointOfSaleId', enterprise?.roomServicePointOfSaleId);

        PartnerPmsService.ListPointOfSaleItems(formData).then(resp => {
            state.update('items', resp.data.items as IPointOfSaleItem[]);
        }).catch(err => {
            state.update('items', []);
        });
        // eslint-disable-next-line
    }, [room, enterprise]);


    useEffect(() => {
        if (viewShoppingCartDialog.open && Object.values(state.values.shoppingCart).length === 0) {
            viewShoppingCartDialog.handleClose();
        }
        // eslint-disable-next-line
    }, [state.values.shoppingCart]);


    const filteredItems = useMemo(() => {
        let itemsAux = state.values.items;

        if (search.value.trim().length > 0) {
            const value = StringHelper.Normalize(search.value.toUpperCase());
            itemsAux = itemsAux.filter(p => StringHelper.Normalize(p.name.toUpperCase()).includes(value));
        }

        return itemsAux;
    }, [search.value, state.values.items]);


    const handleUpdateShoppingCart = useCallback((amount: number, item: IPointOfSaleItem) => {
        const shoppingCartAux = {
            ...state.values.shoppingCart,
            [item.id]: {
                ...item,
                amount: amount
            }
        };

        state.update('shoppingCart', Object.entries(shoppingCartAux).filter(([key, item]) => {
                return item.amount > 0;
            }).reduce((acc, curr) => {
                const key = curr[0];
                const item = curr[1];
                return {
                    ...acc,
                    [key]: item
                }; 
            }, {})
        );
        // eslint-disable-next-line
    }, [state.values.shoppingCart]);


    const headerActionsRight = useMemo(() => {
        const elements = [] as JSX.Element[];

        if (state.values.items.length > 0) {
            if (search.open) {
                elements.push(<Box marginRight={1}>
                    <IconButton size='small' onClick={() => search.handleClose()}>
                        <SearchOff fontSize='inherit' />
                    </IconButton>
                </Box>);
            } else {
                elements.push(<Box marginRight={1}>
                    <IconButton size='small' onClick={() => search.handleOpen()}>
                        <SearchOn fontSize='inherit' />
                    </IconButton>
                </Box>);
            }

            elements.push(<Search
                open={search.open}
                value={search.value}
                handleChange={(e) => search.handleChangeValue(e.currentTarget.value)} 
            />);
        }

        return elements;
        // eslint-disable-next-line
    }, [state.values.items, search, translate]);


    const dialogActions = useMemo(() => {
        const elements = [
            <ActionButton action='close' onClick={handleClose} />
        ];

        if (state.values.items.length > 0) {
            const items = Object.values(state.values.shoppingCart).length;

            elements.push(<Badge badgeContent={items} showZero={true} className='backgroundcolor-secondary color-white'>
                <ActionButton action='viewShoppingCart' disabled={items === 0} onClick={() => viewShoppingCartDialog.handleOpen()} />
            </Badge>);
        }

        return elements;
        // eslint-disable-next-line
    }, [state.values, translate]);


    const dialogClassName = useMemo(() => {
        const className = ['overflow-inherit', 'roomserviceitemslist-dialog'];
        className.push(state.values.items.length > 0 ? 'dialogactions-spacebetween' : 'dialogactions-flexend');

        if (search.open) {
            className.push('search-active')
        }

        return className.join(' ');
    }, [state.values.items.length, search.open]);
    
    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate('launchItems')}
                headerActionsRight={headerActionsRight}
                dialogActions={dialogActions}
                className={dialogClassName}
                absolute={true}
                dividers={true}
            >
                <Grid container>
                    <Grid item xs={12}>
                        {
                            filteredItems.length === 0 ? (
                                <Empty text='noItemsFound' />
                            ) : (
                                <PointOfSaleItemsList 
                                    items={filteredItems}
                                    actions={item => {
                                        const itemOnCart = state.values.shoppingCart[item.id] ?? undefined;
                                        const amount = itemOnCart?.amount ?? 0;
                                        const disabled = amount === 0;

                                        return <>
                                            <Button 
                                                className={`item-action ${disabled ? 'backgroundcolor-grey-2' : 'color-red'}`} 
                                                disabled={disabled}
                                                onClick={() => handleUpdateShoppingCart(amount - 1, item)}
                                            >
                                                <Remove />
                                            </Button>

                                            <Typography className='item-amount'> 
                                                {amount}
                                            </Typography>

                                            <Button 
                                                className='item-action color-primary'
                                                onClick={() => handleUpdateShoppingCart(amount + 1, item)}
                                            >
                                                <Add />
                                            </Button>
                                        </>
                                    }}
                                />
                            )
                        }
                    </Grid>
                </Grid>
            </Dialog>

            <ViewShoppingCartDialog
                open={viewShoppingCartDialog.open}
                handleClose={() => viewShoppingCartDialog.handleClose()}
                shoppingCart={Object.values(state.values.shoppingCart)}
                handleRemoveItem={(item) => handleUpdateShoppingCart(0, item)}
                handleSubmitSuccess={() => {
                    state.update('shoppingCart', {});
                    handleClose();
                }}
                room={room}
                reservation={reservation}
            />
        </>
    );
}

export default RoomServiceItemsListDialog;